<template>
    <div class="TalentBaseJobPreferences">
        <div v-if="jobPreferences">
            <CCardBox>
                <template v-if="jobPreferences.jobType?.length">
                    <CFormGroup label="Druh úväzku">
                        <CList>
                            <li v-for="item in jobPreferences.jobType" :key="item">
                                {{ getLabelFromId(item, jobTypeOptions) }}
                            </li>
                        </CList>
                    </CFormGroup>
                </template>

                <template v-if="jobPreferences.formOfCollaboration?.length">
                    <CLineBreak class="my-5" />

                    <CFormGroup label="Forma spolupráce">
                        <CList>
                            <li v-for="item in jobPreferences.formOfCollaboration" :key="item">
                                {{ getLabelFromId(item, formOfCollaborationOptions) }}
                            </li>
                        </CList>
                    </CFormGroup>
                </template>

                <template v-if="jobPreferences.jobTime?.length">
                    <CLineBreak class="my-5" />

                    <CFormGroup label="Pracovný čas">
                        <CList>
                            <li v-for="item in jobPreferences.jobTime" :key="item">
                                {{ getLabelFromId(item, jobTimeOptions) }}
                            </li>
                        </CList>
                    </CFormGroup>
                </template>

                <template v-if="jobPreferences.jobPlace?.length">
                    <CLineBreak class="my-5" />

                    <CFormGroup label="Pracovné miesto">
                        <CList>
                            <li v-for="item in jobPreferences.jobPlace" :key="item">
                                {{ `${getLabelFromId(item, jobPlaceOptions)}${getJobLocation(item)}` }}
                            </li>
                        </CList>
                    </CFormGroup>
                </template>

                <template v-if="jobPreferences.moreAboutFlexibility">
                    <CLineBreak class="my-5" />

                    <CFormGroup label="Viac o mojej flexibilite:">
                        <div>
                            <span ref="flexibilityRef" class="dots">{{ jobPreferences.moreAboutFlexibility }}</span>
                            <CButton v-if="isOverflowing" class="!min-h-0 !p-0" variant="link" @click="showMoreText">
                                čítať viac
                            </CButton>
                        </div>
                    </CFormGroup>
                </template>
            </CCardBox>
        </div>

        <div v-else>Žiadne preferencie</div>
    </div>
</template>

<script setup lang="ts">
import debounce from 'lodash-es/debounce'
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'

import { formOfCollaborationList, jobPlaceList, jobTimeList, jobTypesList } from '#shared/types/education'
import { JobPlaceType, JobType } from '#shared/types/Job'
import { optionType } from '#shared/types/JobRequest'
import type { TalentBaseUserProfile } from '#shared/types/User'

const props = defineProps<{
    jobPreferences?: TalentBaseUserProfile['jobPreferences']
}>()

const { t: $t, t } = useI18n()
const isMounted = ref(false)
const trigger = ref(false)
const flexibilityRef = ref<HTMLElement | null>(null)

const jobTypeOptions = computed<optionType[]>(() => {
    return jobTypesList
        .filter((item) => item.id !== JobType.unpaidInternship && item.id !== JobType.paidInternship)
        .map((item) => ({
            ...item,
            label: t(`jobForm.fields.type.values.${item.id}`, 'sk').toString()
        }))
})

const jobPlaceOptions = computed<optionType[]>(() => {
    return jobPlaceList.map((item) => ({
        ...item,
        label: t(`jobForm.fields.flexi.fields.jobPlace.values.${item.id}`, 'sk').toString()
    }))
})

const formOfCollaborationOptions = computed<optionType[]>(() => {
    return formOfCollaborationList.map((item) => ({
        ...item,
        label: t(`jobForm.fields.formOfCollaboration.values.${item.id}`, 'sk').toString()
    }))
})

const jobTimeOptions = computed<optionType[]>(() => {
    return jobTimeList.map((item) => ({
        ...item,
        label: t(`jobForm.fields.flexi.fields.jobTime.values.${item.id}`, 'sk').toString()
    }))
})

const isOverflowing = computed(() => {
    if (!flexibilityRef.value || !props.jobPreferences?.moreAboutFlexibility) return false
    switchTrigger()
    return flexibilityRef.value.scrollHeight > flexibilityRef.value.offsetHeight
})

onMounted(() => {
    isMounted.value = true

    nextTick(() => {
        window.addEventListener('resize', watchResize())
    })
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', watchResize())
})

const switchTrigger = () => {
    trigger.value = !trigger.value
}

const getLabelFromId = (itemId: string, array: optionType[]): string => {
    return array.find(({ id }) => itemId === id)?.label || ''
}

const getJobLocation = (jobPlace: JobPlaceType): string => {
    if (jobPlace !== JobPlaceType.office || !props.jobPreferences?.jobLocation?.length) {
        return ''
    }

    return `: ${props.jobPreferences.jobLocation.join(', ')}`
}

const showMoreText = () => {
    if (!flexibilityRef.value) return
    flexibilityRef.value.classList.remove('dots')
    trigger.value = !trigger.value
}

const onResize = () => {
    trigger.value = !trigger.value
}

const watchResize = () => {
    return debounce(onResize, 500)
}
</script>

<style scoped>
.dots {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}
</style>
